import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getCustomers, getCustomerHistory, getSaleItems, createCustomer, updateCustomer, addPayment, getLedger } from '../services/api';
import Layout from '../components/Layout';
import apiService from '../services/api';
import { debounce } from 'lodash';
import { showSuccess, showError } from '../services/swal';
import { printInvoice } from '../services/printService';

const Customers = () => {
    const [customers, setCustomers] = useState([]);
    const [search, setSearch] = useState('');
    const [selectedCustomer, setSelectedCustomer] = useState(null);
    const [history, setHistory] = useState([]);
    const [ledger, setLedger] = useState([]);
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [ledgerLoading, setLedgerLoading] = useState(false);
    const [expandedInvoices, setExpandedInvoices] = useState({});
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showPaymentModal, setShowPaymentModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importProgress, setImportProgress] = useState(0);
    const [importStats, setImportStats] = useState({ current: 0, total: 0 });
    const [newCustomer, setNewCustomer] = useState({ name: '', phone: '', email: '', address: '' });
    const [editingCustomer, setEditingCustomer] = useState(null);
    const [paymentAmount, setPaymentAmount] = useState('');
    const [sortBy, setSortBy] = useState('balance');
    const [activeTab, setActiveTab] = useState('history');
    const [exportLoading, setExportLoading] = useState(false);
    const [importReport, setImportReport] = useState(null);
    const [showImportReportModal, setShowImportReportModal] = useState(false);

    const handleExport = async () => {
        setExportLoading(true);
        try {
            const res = await apiService.get('/customers/export', { responseType: 'blob' });
            const blob = new Blob([res.data]);
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `customers_export_${new Date().toISOString().split('T')[0]}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            showSuccess('Export Complete', 'Customer data has been downloaded.');
        } catch (err) {
            showError('Export Failed', 'Could not generate export file. Please try again.');
        } finally {
            setExportLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedFetch = useCallback(debounce((s, sb) => {
        setLoading(true);
        const sortOrder = sb === 'balance' ? 'desc' : 'asc';
        getCustomers({ search: s, sort_by: sb, sort_order: sortOrder })
            .then(res => setCustomers(res.data.data || []))
            .catch(err => {
                console.error("Failed to fetch customers", err);
                setCustomers([]);
            })
            .finally(() => setLoading(false));
    }, 300), []);

    useEffect(() => {
        debouncedFetch(search, sortBy);
    }, [search, sortBy, debouncedFetch]);

    const handleViewDetails = async (customer) => {
        setSelectedCustomer(customer);
        setExpandedInvoices({});
        setActiveTab('history');

        setHistoryLoading(true);
        try {
            const res = await getCustomerHistory(customer.id);
            const fullCustomerDetails = { ...customer, ...res.data.stats };
            setSelectedCustomer(fullCustomerDetails);
            setHistory(res.data.sales || []);
        } catch (err) {
            console.error("Failed to fetch history", err);
            setHistory([]);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleTabClick = async (tab) => {
        setActiveTab(tab);
        if (tab === 'ledger' && selectedCustomer) {
            setLedgerLoading(true);
            try {
                const res = await getLedger(selectedCustomer.id);
                setLedger(res.data || []);
            } catch (err) {
                console.error("Failed to fetch ledger", err);
                setLedger([]);
            } finally {
                setLedgerLoading(false);
            }
        }
    };

    const toggleInvoice = async (saleId) => {
        const isExpanded = !!expandedInvoices[saleId];
        if (!isExpanded) {
            const sale = history.find(s => s.id === saleId);
            if (sale && !sale.items) {
                try {
                    const res = await getSaleItems(saleId);
                    setHistory(currentHistory => currentHistory.map(s => s.id === saleId ? { ...s, items: res.data } : s));
                } catch (err) {
                    console.error("Failed to fetch sale items", err);
                }
            }
        }
        setExpandedInvoices(prev => ({ ...prev, [saleId]: !isExpanded }));
    };

    const handleCreateCustomer = async () => {
        try {
            await createCustomer(newCustomer);
            setShowAddModal(false);
            setNewCustomer({ name: '', phone: '', email: '', address: '' });
            debouncedFetch(search, sortBy);
            showSuccess('Customer Created!', 'New customer has been added successfully.');
        } catch (err) {
            showError('Failed to Create', err.response?.data?.message || err.message);
        }
    };

    const handleEditCustomer = (customer) => {
        setEditingCustomer({ ...customer });
        setShowEditModal(true);
    };

    const handleUpdateCustomer = async () => {
        if (!editingCustomer) return;
        try {
            const updatedData = await updateCustomer(editingCustomer.id, editingCustomer);
            setShowEditModal(false);
            debouncedFetch(search, sortBy);
            if (selectedCustomer && selectedCustomer.id === editingCustomer.id) {
                handleViewDetails(updatedData.data);
            }
            showSuccess('Customer Updated!', 'Customer details have been saved.');
        } catch (err) {
            showError('Failed to Update', err.response?.data?.message || err.message);
        }
    };

    const handleAddPayment = async () => {
        if (!selectedCustomer || !paymentAmount) return;
        try {
            const res = await addPayment(selectedCustomer.id, { amount: paymentAmount });
            setShowPaymentModal(false);
            setPaymentAmount('');
            handleViewDetails(res.data);
            debouncedFetch(search, sortBy);
            showSuccess('Payment Added!', 'Payment has been recorded successfully.');
        } catch (err) {
            showError('Payment Failed', err.response?.data?.message || err.message);
        }
    };

    const handleImport = async (e) => {
        e.preventDefault();
        if (!importFile) return;

        setImportLoading(true);
        setImportProgress(0);
        setImportReport(null);

        const reader = new FileReader();
        reader.onload = async (event) => {
            const csvData = event.target.result;
            const lines = csvData.split(/\r?\n/).filter(line => line.trim() !== "");
            if (lines.length <= 1) {
                showError('Empty File', 'The CSV file has no data.');
                setImportLoading(false);
                return;
            }

            const header = lines[0];
            const rows = lines.slice(1);
            const totalRows = rows.length;
            setImportStats({ current: 0, total: totalRows });

            const chunkSize = 100;
            let finalStats = { imported: 0, updated: 0, skipped: 0, errors: 0, total_rows: 0 };
            let allErrors = [];

            for (let i = 0; i < rows.length; i += chunkSize) {
                const chunkRows = rows.slice(i, i + chunkSize);
                const chunkCsv = [header, ...chunkRows].join("\n");
                
                const formData = new FormData();
                const blob = new Blob([chunkCsv], { type: 'text/csv' });
                formData.append('file', blob, 'chunk.csv');

                try {
                    const res = await apiService.post('/import/customers', formData, {
                        headers: { 'Content-Type': 'multipart/form-data' }
                    });
                    const report = res.data;
                    
                    finalStats.imported += report.stats.imported;
                    finalStats.skipped += report.stats.skipped;
                    finalStats.errors += report.stats.errors;
                    finalStats.total_rows += report.stats.total_rows;
                    
                    if (report.error_details) {
                        const adjustedErrors = report.error_details.map(err => ({
                            ...err,
                            row: err.row + i
                        }));
                        allErrors = [...allErrors, ...adjustedErrors];
                    }

                    const progress = Math.min(Math.round(((i + chunkRows.length) / totalRows) * 100), 100);
                    setImportProgress(progress);
                    setImportStats(prev => ({ ...prev, current: Math.min(i + chunkRows.length, totalRows) }));

                } catch (err) {
                    console.error('Chunk import failed:', err);
                }
            }

            setImportReport({ stats: finalStats, error_details: allErrors });
            setShowImportModal(false);
            setShowImportReportModal(true);
            setImportFile(null);
            setImportLoading(false);
            debouncedFetch(search, sortBy);
        };
        reader.readAsText(importFile);
    };

    const exportImportReportCSV = () => {
        if (!importReport || !importReport.error_details.length) return;
        const headers = ["Row", "Name", "Phone", "Reason"];
        const rows = importReport.error_details.map(e => [e.row, `"${e.name || ''}"`, `"${e.phone || ''}"`, `"${e.error}"`]);
        const csvContent = [headers.join(","), ...rows.map(r => r.join(","))].join("\n");
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `customer_import_errors_${new Date().getTime()}.csv`;
        a.click();
    };

    const exportImportReportPDF = () => {
        const printContent = `
            <html>
                <head>
                    <title>Customer Import Failure Report</title>
                    <style>
                        body { font-family: 'Inter', sans-serif; padding: 40px; color: #1e1e2d; }
                        h1 { color: #f1416c; border-bottom: 2px solid #eff2f5; padding-bottom: 15px; font-weight: 800; }
                        .stats { display: flex; gap: 20px; margin: 30px 0; }
                        .stat-box { flex: 1; padding: 20px; border-radius: 12px; background: #f9f9f9; text-align: center; border: 1px solid #eff2f5; }
                        .stat-val { font-size: 28px; font-weight: 800; margin-bottom: 5px; }
                        .stat-label { font-size: 12px; font-weight: 700; text-transform: uppercase; color: #a1a5b7; }
                        table { width: 100%; border-collapse: collapse; margin-top: 30px; }
                        th, td { border: 1px solid #eff2f5; padding: 15px; text-align: left; }
                        th { background: #f5f8fa; font-weight: 700; color: #5e6278; font-size: 13px; }
                        .row-num { font-weight: 800; color: #7e8299; width: 60px; }
                    </style>
                </head>
                <body>
                    <h1>Customer Import Failure Report</h1>
                    <p style="color: #a1a5b7; font-weight: 600;">Generated on: ${new Date().toLocaleString()}</p>
                    <div class="stats">
                        <div class="stat-box"><div class="stat-val" style="color: #50cd89">${importReport.stats.imported}</div><div class="stat-label">Imported</div></div>
                        <div class="stat-box"><div class="stat-val" style="color: #ffc700">${importReport.stats.skipped}</div><div class="stat-label">Skipped</div></div>
                        <div class="stat-box"><div class="stat-val" style="color: #f1416c">${importReport.stats.errors}</div><div class="stat-label">Errors</div></div>
                    </div>
                    <table>
                        <thead><tr><th>Row</th><th>Customer Name</th><th>Phone</th><th>Failure Reason</th></tr></thead>
                        <tbody>
                            ${importReport.error_details.map(e => `
                                <tr>
                                    <td class="row-num">#${e.row}</td>
                                    <td>${e.name || '—'}</td>
                                    <td>${e.phone || '—'}</td>
                                    <td style="color: #f1416c; font-weight: 600;">${e.error}</td>
                                </tr>
                            `).join('')}
                        </tbody>
                    </table>
                    <div style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #eff2f5; text-align: center; color: #a1a5b7; font-size: 11px; font-weight: 600;">
                        System Report • appy.pk POS
                    </div>
                </body>
            </html>
        `;
        const win = window.open('', '_blank');
        win.document.write(printContent);
        win.document.close();
        win.print();
    };

    const getInitials = (name) => {
        return name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
    };

    const colors = ['#6C5DD3', '#FFA500', '#00C4DF', '#FF4B5C', '#27AE60', '#F2994A'];
    const getAvatarColor = (name) => colors[name.length % colors.length];

    const stats = useMemo(() => {
        const total = customers.length;
        const withBalance = customers.filter(c => c.balance > 0).length;
        const totalReceivable = customers.reduce((sum, c) => sum + parseFloat(c.balance || 0), 0);
        return { total, withBalance, totalReceivable };
    }, [customers]);

    return (
        <Layout title="Customer Relations">
            {/* CRM Insights */}
            <div className="row g-4 mb-4">
                <div className="col-12 col-md-4">
                    <div className="category-stat-card shadow-sm border-0">
                        <div className="icon-badge bg-primary bg-opacity-10 text-primary">
                            <i className="bi bi-people-fill"></i>
                        </div>
                        <div>
                            <small className="text-muted d-block fw-bold mb-1">Total Customers</small>
                            <h4 className="fw-bold mb-0">{stats.total}</h4>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-4">
                    <div className="category-stat-card shadow-sm border-0">
                        <div className="icon-badge bg-danger bg-opacity-10 text-danger">
                            <i className="bi bi-exclamation-triangle-fill"></i>
                        </div>
                        <div>
                            <small className="text-muted d-block fw-bold mb-1">Active Receivables</small>
                            <h4 className="fw-bold mb-0">{stats.withBalance} Users</h4>
                        </div>
                    </div>
                </div>
                <div className="col-12 col-md-4">
                    <div className="category-stat-card shadow-sm border-0 bg-primary bg-opacity-10 border-primary border-opacity-10">
                        <div className="icon-badge bg-primary text-white">
                            <i className="bi bi-cash-stack"></i>
                        </div>
                        <div>
                            <small className="text-primary-emphasis d-block fw-bold mb-1">Total Due Amount</small>
                            <h4 className="fw-bold mb-0">Rs. {stats.totalReceivable.toLocaleString()}</h4>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                {/* Customer List Section */}
                <div className="col-12 col-xxl-4 col-xl-5">
                    <div className="customer-list-container shadow-sm border-0">
                        <div className="p-3 border-bottom bg-white sticky-top">
                            <div className="d-flex gap-2 mb-3 management-cluster">
                                <div className="search-wrapper position-relative flex-grow-1">
                                    <i className="bi bi-search position-absolute top-50 start-0 translate-middle-y ms-3 text-muted" style={{ zIndex: 10 }}></i>
                                    <input
                                        type="text"
                                        className="form-control pos-search-input"
                                        placeholder="Find customer by name or phone..."
                                        value={search}
                                        onChange={(e) => { setSearch(e.target.value); }}
                                        style={{ 
                                            height: '52px', 
                                            borderRadius: '16px', 
                                            paddingLeft: '3.2rem', 
                                            border: '2px solid var(--border-color)', 
                                            background: '#fcfcfd',
                                            transition: 'all 0.3s ease',
                                            fontSize: '0.95rem'
                                        }}
                                    />
                                </div>
                                <div className="d-flex gap-2">
                                    <button className="btn btn-primary rounded-3 px-3" onClick={() => setShowAddModal(true)} style={{ height: '52px' }} title="Add Customer">
                                        <i className="bi bi-person-plus-fill"></i>
                                    </button>
                                    <button className="btn btn-light border rounded-3 px-3" onClick={() => setShowImportModal(true)} style={{ height: '52px' }} title="Import CSV">
                                        <i className="bi bi-file-earmark-arrow-up"></i>
                                    </button>
                                    <button className="btn btn-light border rounded-3 px-3" onClick={handleExport} disabled={exportLoading} style={{ height: '52px' }} title="Export CSV">
                                        {exportLoading ? <span className="spinner-border spinner-border-sm"></span> : <i className="bi bi-file-earmark-arrow-down"></i>}
                                    </button>
                                </div>
                            </div>
                            <div className="d-flex gap-2">
                                <button className={`btn btn-sm flex-grow-1 rounded-pill ${sortBy === 'balance' ? 'btn-primary' : 'btn-light border'}`} onClick={() => setSortBy('balance')}>
                                    High Due First
                                </button>
                                <button className={`btn btn-sm flex-grow-1 rounded-pill ${sortBy === 'name' ? 'btn-primary' : 'btn-light border'}`} onClick={() => setSortBy('name')}>
                                    Alphabetic
                                </button>
                            </div>
                        </div>
                        <div className="customer-scroll-area">
                            {loading ? (
                                <div className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></div>
                            ) : customers.length === 0 ? (
                                <div className="text-center py-5 text-muted">No customers found</div>
                            ) : (
                                customers.map(c => (
                                    <div key={c.id} className={`customer-item ${selectedCustomer?.id === c.id ? 'active' : ''}`} onClick={() => handleViewDetails(c)}>
                                        <div className="d-flex align-items-center gap-3">
                                            <div className="avatar-initial text-white" style={{ background: getAvatarColor(c.name) }}>
                                                {getInitials(c.name)}
                                            </div>
                                            <div className="flex-grow-1 min-w-0">
                                                <h6 className="fw-bold mb-0 text-truncate">{c.name}</h6>
                                                <small className="text-muted d-block">{c.phone}</small>
                                            </div>
                                            <div className="text-end">
                                                <div className={`fw-bold ${c.balance > 0 ? 'text-danger' : 'text-success'}`}>
                                                    Rs. {parseFloat(c.balance).toLocaleString()}
                                                </div>
                                                {c.balance > 0 && <span className="badge bg-danger bg-opacity-10 text-danger border-0 small px-2 py-1">Due</span>}
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* Customer Detail Section */}
                <div className="col-12 col-xxl-8 col-xl-7">
                    {selectedCustomer ? (
                        <div className="customer-detail-pane shadow-sm border-0">
                            {/* Profile Header */}
                            <div className="p-4 border-bottom bg-white sticky-top rounded-top-4">
                                <div className="d-flex justify-content-between align-items-center gap-4 customer-profile-header">
                                    <div className="d-flex align-items-center gap-4">
                                        <div className="avatar-initial text-white" style={{ width: '72px', height: '72px', fontSize: '1.8rem', background: getAvatarColor(selectedCustomer.name) }}>
                                            {getInitials(selectedCustomer.name)}
                                        </div>
                                        <div>
                                            <h3 className="fw-bold mb-1 text-dark">{selectedCustomer.name}</h3>
                                            <div className="d-flex flex-wrap gap-3 text-muted small align-items-center">
                                                <a href={`tel:${selectedCustomer.phone}`} className="text-decoration-none text-muted hover-primary"><i className="bi bi-telephone-fill me-2"></i>{selectedCustomer.phone}</a>
                                                <a href={`https://wa.me/${selectedCustomer.phone.replace(/\D/g,'')}`} target="_blank" rel="noopener noreferrer" className="text-success text-decoration-none fw-bold"><i className="bi bi-whatsapp me-1"></i> WhatsApp</a>
                                                {selectedCustomer.email && <span><i className="bi bi-envelope-fill me-2"></i>{selectedCustomer.email}</span>}
                                            </div>
                                        </div>
                                    </div>
                                    <div className="d-flex gap-2">
                                        <button className="btn btn-primary px-4 py-2 rounded-pill fw-bold shadow-sm" onClick={() => setShowPaymentModal(true)}>
                                            <i className="bi bi-cash-coin me-2"></i> Settle Due
                                        </button>
                                        <button className="btn btn-light border px-4 py-2 rounded-pill fw-bold" onClick={() => handleEditCustomer(selectedCustomer)}>
                                            <i className="bi bi-pencil-square me-2"></i> Edit
                                        </button>
                                    </div>
                                </div>

                                <div className="mt-4 pt-2">
                                    <div className="nav nav-tabs border-0 gap-4" style={{ marginBottom: '-1px' }}>
                                        {['history', 'ledger', 'analytics'].map(tab => (
                                            <button 
                                                key={tab}
                                                className={`nav-link border-0 px-0 py-2 fw-bold text-capitalize ${activeTab === tab ? 'active border-bottom border-primary border-3 text-primary' : 'text-muted'}`}
                                                onClick={() => handleTabClick(tab)}
                                                style={{ background: 'transparent' }}
                                            >
                                                {tab === 'history' ? 'Order History' : tab === 'ledger' ? 'Payment Ledger' : 'Insight Analytics'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* Tab Content */}
                            <div className="p-4">
                                {activeTab === 'history' && (
                                    <div className="row g-3">
                                        {historyLoading ? (
                                            <div className="text-center py-5"><div className="spinner-border text-primary"></div></div>
                                        ) : history.length === 0 ? (
                                            <div className="text-center py-5 text-muted">
                                                <i className="bi bi-journal-x fs-1 opacity-25 d-block mb-3"></i>
                                                No purchase history found for this customer.
                                            </div>
                                        ) : (
                                            history.map(sale => (
                                                <div key={sale.id} className="card border shadow-none rounded-4 mb-3 overflow-hidden">
                                                    <div className="card-header bg-light bg-opacity-50 border-0 p-3" onClick={() => toggleInvoice(sale.id)} style={{ cursor: 'pointer' }}>
                                                        <div className="d-flex justify-content-between align-items-center">
                                                            <div className="d-flex align-items-center gap-3">
                                                                <div className="icon-badge bg-white text-primary border shadow-sm" style={{ width: '40px', height: '40px' }}>
                                                                    <i className="bi bi-receipt"></i>
                                                                </div>
                                                                <div>
                                                                    <div className="fw-bold">Invoice #{sale.invoice_number}</div>
                                                                    <small className="text-muted">{new Date(sale.created_at).toLocaleDateString()} at {new Date(sale.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</small>
                                                                </div>
                                                            </div>
                                                            <div className="text-end">
                                                                <div className="fw-bold text-dark mb-1">Rs. {parseFloat(sale.final_amount).toLocaleString()}</div>
                                                                <span className={`timeline-badge ${sale.payment_status === 'paid' ? 'bg-success bg-opacity-10 text-success' : 'bg-warning bg-opacity-10 text-warning'}`}>
                                                                    {sale.payment_status.toUpperCase()}
                                                                </span>
                                                            </div>
                                                        </div>
                                                    </div>
                                                    {expandedInvoices[sale.id] && (
                                                        <div className="card-body bg-white p-0 border-top">
                                                            <div className="table-responsive">
                                                                <table className="table table-sm align-middle mb-0">
                                                                    <thead className="bg-light small">
                                                                        <tr>
                                                                            <th className="ps-4">Product</th>
                                                                            <th className="text-center">Qty</th>
                                                                            <th className="text-end">Unit Price</th>
                                                                            <th className="text-end pe-4">Total</th>
                                                                        </tr>
                                                                    </thead>
                                                                    <tbody>
                                                                        {sale.items?.map(item => (
                                                                            <tr key={item.id}>
                                                                                <td className="ps-4 py-3 fw-medium">{item.product_name}</td>
                                                                                <td className="text-center">{item.quantity}</td>
                                                                                <td className="text-end">Rs. {parseFloat(item.unit_price).toLocaleString()}</td>
                                                                                <td className="text-end pe-4 fw-bold">Rs. {parseFloat(item.total_price).toLocaleString()}</td>
                                                                            </tr>
                                                                        ))}
                                                                    </tbody>
                                                                </table>
                                                            </div>
                                                            <div className="p-3 bg-light bg-opacity-25 d-flex justify-content-between align-items-center">
                                                                <button className="btn btn-sm btn-outline-primary rounded-pill px-3 fw-bold" onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    printInvoice({
                                                                        invoice_number: sale.invoice_number,
                                                                        customer_name: selectedCustomer.name,
                                                                        customer_phone: selectedCustomer.phone,
                                                                        items: sale.items || [],
                                                                        final_amount: sale.final_amount,
                                                                        paid_amount: sale.paid_amount,
                                                                        due_amount: sale.due_amount,
                                                                        date: sale.created_at
                                                                    });
                                                                }}>
                                                                    <i className="bi bi-printer me-1"></i> Print Invoice
                                                                </button>
                                                                <div className="d-flex gap-5">
                                                                <div className="text-end">
                                                                    <small className="text-muted d-block">Paid</small>
                                                                    <span className="fw-bold text-success">Rs. {parseFloat(sale.paid_amount).toLocaleString()}</span>
                                                                </div>
                                                                {parseFloat(sale.due_amount) > 0 && (
                                                                    <div className="text-end">
                                                                        <small className="text-muted d-block">Outstanding</small>
                                                                        <span className="fw-bold text-danger">Rs. {parseFloat(sale.due_amount).toLocaleString()}</span>
                                                                    </div>
                                                                )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                )}

                                {activeTab === 'ledger' && (
                                    <div className="card border rounded-4 overflow-hidden">
                                        <div className="table-responsive">
                                            <table className="table ledger-table align-middle mb-0">
                                                <thead>
                                                    <tr className="text-muted small">
                                                        <th className="ps-4">Transaction Date</th>
                                                        <th>Type / Ref</th>
                                                        <th className="text-end">Debit (Purchase)</th>
                                                        <th className="text-end">Credit (Payment)</th>
                                                        <th className="text-end pe-4">Current Balance</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {ledgerLoading ? (
                                                        <tr><td colSpan="5" className="text-center py-5"><div className="spinner-border spinner-border-sm text-primary"></div></td></tr>
                                                    ) : ledger.length === 0 ? (
                                                        <tr><td colSpan="5" className="text-center py-5 text-muted">No transactions found.</td></tr>
                                                    ) : (
                                                        ledger.map((item, idx) => {
                                                            const runningBalance = ledger.slice(0, idx + 1).reduce((acc, curr) => 
                                                                curr.type === 'sale' ? acc + parseFloat(curr.amount) : acc - parseFloat(curr.amount), 0);
                                                            return (
                                                                <tr key={`${item.type}-${item.id}`}>
                                                                    <td className="ps-4">
                                                                        <div className="fw-bold">{new Date(item.created_at).toLocaleDateString()}</div>
                                                                        <small className="text-muted">{new Date(item.created_at).toLocaleTimeString()}</small>
                                                                    </td>
                                                                    <td>
                                                                        <span className={`timeline-badge ${item.type === 'sale' ? 'bg-primary bg-opacity-10 text-primary' : 'bg-success bg-opacity-10 text-success'}`}>
                                                                            {item.type === 'sale' ? `Invoice #${item.invoice_number}` : 'Cash Received'}
                                                                        </span>
                                                                    </td>
                                                                    <td className="text-end text-danger">{item.type === 'sale' ? `+ Rs. ${parseFloat(item.amount).toLocaleString()}` : '-'}</td>
                                                                    <td className="text-end text-success">{item.type === 'payment' ? `- Rs. ${parseFloat(item.amount).toLocaleString()}` : '-'}</td>
                                                                    <td className="text-end pe-4 fw-bold">Rs. {runningBalance.toLocaleString()}</td>
                                                                </tr>
                                                            );
                                                        })
                                                    )}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}

                                {activeTab === 'analytics' && (
                                    <div className="row g-4">
                                        <div className="col-12 col-md-6 col-xl-4">
                                            <div className="card border-0 bg-light rounded-4 p-4 h-100">
                                                <div className="icon-badge bg-white text-primary mb-3 shadow-sm"><i className="bi bi-graph-up-arrow"></i></div>
                                                <small className="text-muted fw-bold d-block mb-1">Lifetime Value</small>
                                                <h4 className="fw-bold mb-0">Rs. {parseFloat(selectedCustomer.lifetime_value || 0).toLocaleString()}</h4>
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-xl-4">
                                            <div className="card border-0 bg-light rounded-4 p-4 h-100">
                                                <div className="icon-badge bg-white text-success mb-3 shadow-sm"><i className="bi bi-cart-check-fill"></i></div>
                                                <small className="text-muted fw-bold d-block mb-1">Total Products</small>
                                                <h4 className="fw-bold mb-0">{selectedCustomer.total_items || 0} Units</h4>
                                            </div>
                                        </div>
                                        <div className="col-12 col-md-6 col-xl-4">
                                            <div className="card border-0 bg-light rounded-4 p-4 h-100">
                                                <div className="icon-badge bg-white text-warning mb-3 shadow-sm"><i className="bi bi-star-fill"></i></div>
                                                <small className="text-muted fw-bold d-block mb-1">Top Category</small>
                                                <h4 className="fw-bold mb-0">{selectedCustomer.most_purchased_category || 'General'}</h4>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="customer-detail-pane d-flex align-items-center justify-content-center text-muted border-0 bg-light bg-opacity-50">
                            <div className="text-center opacity-50">
                                <i className="bi bi-person-badge fs-1 mb-3 d-block"></i>
                                <h5 className="fw-bold">Select a profile</h5>
                                <p>Choose a customer from the left to view detailed history and analytics.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Modals */}
            {(showAddModal || showEditModal) && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1100 }}></div>
                    <div className="modal fade show d-block" style={{ zIndex: 1101 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                                <div className="modal-header border-0 bg-primary text-white p-4">
                                    <h5 className="modal-title fw-bold">{showAddModal ? 'Register New Customer' : 'Update Profile Details'}</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => { setShowAddModal(false); setShowEditModal(false); }}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="row g-3">
                                        <div className="col-12">
                                            <label className="form-label small fw-bold text-muted">Full Name</label>
                                            <input type="text" className="form-control rounded-3" value={showAddModal ? newCustomer.name : editingCustomer.name} onChange={e => showAddModal ? setNewCustomer({ ...newCustomer, name: e.target.value }) : setEditingCustomer({ ...editingCustomer, name: e.target.value })} style={{ height: '48px' }} />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label small fw-bold text-muted">Phone Number</label>
                                            <input type="text" className="form-control rounded-3" value={showAddModal ? newCustomer.phone : editingCustomer.phone} onChange={e => showAddModal ? setNewCustomer({ ...newCustomer, phone: e.target.value }) : setEditingCustomer({ ...editingCustomer, phone: e.target.value })} style={{ height: '48px' }} />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label small fw-bold text-muted">Email Address</label>
                                            <input type="email" className="form-control rounded-3" value={showAddModal ? newCustomer.email : editingCustomer.email} onChange={e => showAddModal ? setNewCustomer({ ...newCustomer, email: e.target.value }) : setEditingCustomer({ ...editingCustomer, email: e.target.value })} style={{ height: '48px' }} />
                                        </div>
                                        <div className="col-12">
                                            <label className="form-label small fw-bold text-muted">Postal Address</label>
                                            <textarea className="form-control rounded-3" value={showAddModal ? newCustomer.address : editingCustomer.address} onChange={e => showAddModal ? setNewCustomer({ ...newCustomer, address: e.target.value }) : setEditingCustomer({ ...editingCustomer, address: e.target.value })} style={{ height: '100px' }}></textarea>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 p-4 bg-light">
                                    <button className="btn btn-light px-4 rounded-pill fw-bold" onClick={() => { setShowAddModal(false); setShowEditModal(false); }}>Discard</button>
                                    <button className="btn btn-primary px-4 rounded-pill fw-bold" onClick={showAddModal ? handleCreateCustomer : handleUpdateCustomer}>
                                        {showAddModal ? 'Confirm Registration' : 'Save Changes'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {showPaymentModal && selectedCustomer && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1100 }}></div>
                    <div className="modal fade show d-block" style={{ zIndex: 1101 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                                <div className="modal-header border-0 bg-success text-white p-4">
                                    <h5 className="modal-title fw-bold">Settle Outstanding Due</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowPaymentModal(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="d-flex align-items-center gap-3 p-3 bg-light rounded-4 mb-4">
                                        <div className="avatar-initial bg-success text-white" style={{ width: '56px', height: '56px' }}>
                                            {getInitials(selectedCustomer.name)}
                                        </div>
                                        <div>
                                            <div className="fw-bold fs-5">{selectedCustomer.name}</div>
                                            <div className="text-danger fw-bold">Current Due: Rs. {parseFloat(selectedCustomer.balance).toLocaleString()}</div>
                                        </div>
                                    </div>
                                    <div className="mb-3">
                                        <label className="form-label small fw-bold text-muted">Payment Amount (Rs.)</label>
                                        <input type="number" className="form-control form-control-lg rounded-3 fw-bold text-success" value={paymentAmount} onChange={e => setPaymentAmount(e.target.value)} autoFocus placeholder="0.00" />
                                    </div>
                                    <div className="form-text small"><i className="bi bi-shield-check me-1"></i>This payment will be instantly credited to the customer's ledger.</div>
                                </div>
                                <div className="modal-footer border-0 p-4 bg-light">
                                    <button className="btn btn-light px-4 rounded-pill fw-bold" onClick={() => setShowPaymentModal(false)}>Cancel</button>
                                    <button className="btn btn-success px-4 rounded-pill fw-bold" onClick={handleAddPayment}>Confirm Payment</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {showImportModal && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1100 }}></div>
                    <div className="modal fade show d-block" style={{ zIndex: 1101 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                                <div className="modal-header border-0 bg-primary text-white p-4">
                                    <h5 className="modal-title fw-bold">Bulk Customer Import</h5>
                                    <button type="button" className="btn-close btn-close-white" onClick={() => setShowImportModal(false)}></button>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="alert bg-primary bg-opacity-10 text-primary border-0 rounded-4 p-4 mb-4">
                                        <div className="d-flex align-items-center gap-3 mb-2">
                                            <i className="bi bi-info-circle-fill fs-4"></i>
                                            <h6 className="fw-bold mb-0">CSV Data Mapping</h6>
                                        </div>
                                        <p className="small mb-0 opacity-75">Ensure your CSV contains these columns exactly: <strong>Name, Phone, Email, Address</strong>.</p>
                                    </div>
                                    {importLoading ? (
                                        <div className="py-4 text-center">
                                            <div className="d-flex justify-content-between align-items-center mb-2">
                                                <span className="fw-bold text-primary">Importing Data...</span>
                                                <span className="small fw-bold">{importStats.current} / {importStats.total}</span>
                                            </div>
                                            <div className="progress rounded-pill mb-3" style={{ height: '12px' }}>
                                                <div className="progress-bar progress-bar-striped progress-bar-animated" style={{ width: `${importProgress}%` }}></div>
                                            </div>
                                            <p className="small text-muted mb-0">Processing customer records. This may take a moment.</p>
                                        </div>
                                    ) : (
                                        <form onSubmit={handleImport}>
                                            <div className="mb-4">
                                                <label className="form-label small fw-bold text-muted">CHOOSE CSV FILE</label>
                                                <input type="file" className="form-control" accept=".csv" onChange={e => setImportFile(e.target.files[0])} required style={{ height: '48px' }} />
                                            </div>
                                            <button type="submit" className="btn btn-primary w-100 btn-lg rounded-4 fw-bold py-3">
                                                <i className="bi bi-cloud-arrow-up me-2"></i>
                                                Start Import Process
                                            </button>
                                        </form>
                                    )}
                                    <div className="p-3 bg-light rounded-3 mt-4">
                                        <div className="d-flex align-items-center gap-2 text-muted small">
                                            <i className="bi bi-shield-lock-fill"></i>
                                            <span>Securely encrypted data transmission.</span>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-footer border-0 p-4 bg-light">
                                    <button className="btn btn-light px-4 rounded-pill fw-bold" onClick={() => setShowImportModal(false)}>Abort</button>
                                    <button className="btn btn-primary px-4 rounded-pill fw-bold" onClick={handleImport} disabled={importLoading}>
                                        {importLoading ? <><span className="spinner-border spinner-border-sm me-2"></span>Processing...</> : 'Initiate Import'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}

            {/* Import Report Modal */}
            {showImportReportModal && importReport && (
                <>
                    <div className="modal-backdrop fade show" style={{ zIndex: 1100 }}></div>
                    <div className="modal fade show d-block" style={{ zIndex: 1101 }} tabIndex="-1">
                        <div className="modal-dialog modal-dialog-centered modal-lg">
                            <div className="modal-content border-0 shadow-lg rounded-4 overflow-hidden">
                                <div className="modal-header border-0 bg-primary text-white p-4">
                                    <div className="d-flex align-items-center justify-content-between w-100">
                                        <h5 className="modal-title fw-bold mb-0"><i className="bi bi-clipboard-data me-2"></i>Import Report</h5>
                                        <div className="d-flex gap-2">
                                            <button className="btn btn-sm btn-light rounded-pill px-3 fw-bold" onClick={exportImportReportCSV} disabled={!importReport?.error_details?.length}>
                                                <i className="bi bi-file-earmark-spreadsheet me-1"></i> Export Failures (CSV)
                                            </button>
                                            <button className="btn btn-sm btn-light rounded-pill px-3 fw-bold" onClick={exportImportReportPDF}>
                                                <i className="bi bi-file-earmark-pdf me-1"></i> Export PDF Report
                                            </button>
                                            <button type="button" className="btn-close btn-close-white ms-2" onClick={() => setShowImportReportModal(false)}></button>
                                        </div>
                                    </div>
                                </div>
                                <div className="modal-body p-4">
                                    <div className="row g-3 mb-4">
                                        <div className="col-4">
                                            <div className="text-center p-3 bg-success bg-opacity-10 rounded-4">
                                                <div className="fs-3 fw-bold text-success">{importReport.stats?.imported || 0}</div>
                                                <small className="fw-bold text-muted">Imported</small>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="text-center p-3 bg-warning bg-opacity-10 rounded-4">
                                                <div className="fs-3 fw-bold text-warning">{importReport.stats?.skipped || 0}</div>
                                                <small className="fw-bold text-muted">Skipped</small>
                                            </div>
                                        </div>
                                        <div className="col-4">
                                            <div className="text-center p-3 bg-danger bg-opacity-10 rounded-4">
                                                <div className="fs-3 fw-bold text-danger">{importReport.stats?.errors || 0}</div>
                                                <small className="fw-bold text-muted">Errors</small>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="alert bg-light border-0 rounded-3 p-3 mb-3">
                                        <div className="d-flex align-items-center gap-2">
                                            <i className="bi bi-info-circle text-primary"></i>
                                            <small className="fw-bold">Total Rows Processed: {importReport.stats?.total_rows || 0}</small>
                                        </div>
                                    </div>
                                    {importReport.errors && importReport.errors.length > 0 && (
                                        <div>
                                            <h6 className="fw-bold mb-3"><i className="bi bi-exclamation-triangle text-danger me-2"></i>Error Details</h6>
                                            <div className="table-responsive" style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                                <table className="table table-sm align-middle mb-0">
                                                    <thead className="bg-light small">
                                                        <tr>
                                                            <th>Row</th>
                                                            <th>Name</th>
                                                            <th>Reason</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {importReport.errors.map((err, idx) => (
                                                            <tr key={idx}>
                                                                <td className="fw-bold">#{err.row}</td>
                                                                <td>{err.name || 'N/A'}</td>
                                                                <td><span className="badge bg-danger bg-opacity-10 text-danger">{err.error}</span></td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                                <div className="modal-footer border-0 p-4 bg-light">
                                    <button className="btn btn-primary px-4 rounded-pill fw-bold" onClick={() => setShowImportReportModal(false)}>Close Report</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
            <style>{`
                .customer-list-container {
                    background: var(--card-bg);
                    border-radius: 24px;
                    height: calc(100vh - 200px);
                    display: flex;
                    flex-direction: column;
                    overflow: hidden;
                    border: 1px solid var(--border-color);
                }
                .customer-scroll-area {
                    flex-grow: 1;
                    overflow-y: auto;
                    padding: 10px;
                }
                .customer-item {
                    padding: 15px;
                    border-radius: 16px;
                    cursor: pointer;
                    transition: all 0.2s ease;
                    margin-bottom: 8px;
                    border: 1px solid transparent;
                }
                .customer-item:hover {
                    background: var(--bg-color);
                    transform: translateX(5px);
                }
                .customer-item.active {
                    background: rgba(108, 93, 211, 0.1);
                    border-color: var(--primary-color);
                }
                .avatar-initial {
                    width: 45px;
                    height: 45px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: bold;
                    flex-shrink: 0;
                }
                .customer-detail-pane {
                    background: var(--card-bg);
                    border-radius: 24px;
                    height: calc(100vh - 200px);
                    overflow-y: auto;
                    border: 1px solid var(--border-color);
                }
                .timeline-badge {
                    font-size: 0.7rem;
                    font-weight: 800;
                    padding: 4px 12px;
                    border-radius: 50px;
                    text-transform: uppercase;
                }
                
                /* Dark Mode Support */
                [data-bs-theme="dark"] .bg-white,
                [data-bs-theme="dark"] .bg-light {
                    background-color: var(--card-bg) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .sticky-top {
                    background-color: var(--card-bg) !important;
                }
                [data-bs-theme="dark"] .border-bottom,
                [data-bs-theme="dark"] .border-top,
                [data-bs-theme="dark"] .border {
                    border-color: var(--border-color) !important;
                }
                [data-bs-theme="dark"] .modal-footer,
                [data-bs-theme="dark"] .modal-body .bg-light,
                [data-bs-theme="dark"] .card-header.bg-light {
                    background-color: rgba(255,255,255,0.02) !important;
                }
                [data-bs-theme="dark"] .pos-search-input {
                    background-color: rgba(255,255,255,0.03) !important;
                    color: var(--text-color) !important;
                }
                [data-bs-theme="dark"] .table {
                    --bs-table-bg: transparent;
                    --bs-table-color: var(--text-color);
                }
                [data-bs-theme="dark"] .customer-detail-pane.bg-light {
                    background-color: rgba(0,0,0,0.2) !important;
                }
            `}</style>
        </Layout>
    );
};

export default Customers;
